import {
	CheckCircleFilled,
	CheckCircleOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import {
	Button,
	Modal,
	Popconfirm,
	Result,
	Steps,
	Table,
	Tag,
	Tooltip,
} from 'antd';
import { napsaIntegrationServiceApi } from 'Api/napsaIntegration';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	DeleteActionSvg,
	Paid,
	PaymentUnderProcessWithText,
	PendingForApproval,
	RejectedByAccounts,
} from 'utils/svgs';
import { formatDate, formatNumber, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';


const { Step } = Steps;

type SubmitModalProps = {
	isModalVisible: boolean;
	handleCancel: () => void;
	napsaRecordId: string;
	fetchNapsaReportDetails: any;
};

const steps = [
	{
		title: 'Review NAPSA Returns',
	},
	{
		title: 'Valid NAPSA Returns',
	},
	{
		title: 'Confirmation',
	},
];

// Main component - designed to be directly used in a modal
export const NapsaIntegrationUI = (props: SubmitModalProps) => {
	const {
		isModalVisible,
		handleCancel,
		napsaRecordId,
		fetchNapsaReportDetails,
	} = props;
	const [current, setCurrent] = useState(0);
	const [loading, setLoading] = useState(true); // Start with loading state
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [napsaData, setNapsaData] = useState<any[]>([]);
	const [finalData, setFinalData] = useState<any[]>([]);

	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);

	// Fetch data on initial load
	useEffect(() => {
		fetchData();
	}, []);

	const fetchData = async () => {
		setLoading(true);
		try {
			const { data } = await napsaIntegrationServiceApi.getNapsaRecords({
				napsaRecordId: napsaRecordId,
			});

			setNapsaData(data.data);
		} catch (error: any) {
			const errorMessage =
				error.response.data.message ||
				'An error occurred while fetching data';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	};

	const submitNapsaData = async () => {
		setIsSubmitLoading(true);
		try {
			const employeeIds = finalData.map((item: any) => item.id);
			const body = {
				napsaRecordId: napsaRecordId,
				employeeIds: employeeIds,
			};
			await napsaIntegrationServiceApi.submitNapsaReturn(body);
			toastText('NAPSA data submitted successfully', 'success');
		} catch (error: any) {
			const errorMessage =
				error.response.data.message ||
				'An error occurred while submitting data';
			toastText(errorMessage, 'error');
		} finally {
			setIsSubmitLoading(false);
		}
	};

	const next = async () => {
		if (current == 0) {
			const data = napsaData.filter(
				(item: any) =>
					(item.nrcVerified && item.ssnVerified) ||
					item.status === 'SUCCESS'
			);
			setFinalData(data);
		}
		if (current == 1) {
			submitNapsaData();
		}
		setCurrent((prev: any) => {
			return Math.min(prev + 1, steps.length - 1);
		});
	};

	const prev = () => {
		setCurrent((prev) => Math.max(prev - 1, 0));
	};

	const columns: any = [
		{
			title: 'Employee Name | Code',
			key: 'name',
			fixed: 'left',
			className: 'bg-white',
			width: 200,
			render: (_: any, record: any) =>
				` ${record.firstName || ''} ${record.sirName || ''} ${
					record.lastName || ''
				} | ${record.employeeId || ''}`.trim(),
		},
		{
			title: 'SSN',
			dataIndex: 'ssn',
			key: 'ssn',
			fixed: 'left',
			className: 'bg-white',
			width: 150,
			render: (_: any, record: any) => {
				const isVerified = record.ssnVerified;
				return (
					<span
						style={{
							color: isVerified ? '#019c00' : 'red',
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
						}}
					>
						{record.ssn}
						{isVerified ? (
							<Tooltip title="SSN is Verified">
								<CheckCircleOutlined />
							</Tooltip>
						) : (
							<Tooltip title="SSN is Not Verified">
								<WarningOutlined />
							</Tooltip>
						)}
					</span>
				);
			},
		},

		{
			title: 'NRC',
			dataIndex: 'nrc',
			key: 'nrc',
			className: 'bg-white',
			width: 150,
			render: (_: any, record: any) => {
				const isVerified = record.nrcVerified;
				return (
					<span
						style={{
							color: isVerified ? '#019c00' : 'red',
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
						}}
					>
						{record.nrc}
						{isVerified ? (
							<Tooltip title="NRC is Verified">
								<CheckCircleOutlined />
							</Tooltip>
						) : (
							<Tooltip title="NRC is Not Verified">
								<WarningOutlined />
							</Tooltip>
						)}
					</span>
				);
			},
		},
		{
			title: 'DOB',
			dataIndex: 'dob',
			key: 'dob',
			className: 'bg-white',
			width: 150,
			render: (value: any) => {
				return formatDate(
					value,
					profileData?.companyDateFormat ?? 'DD/MM/YYYY'
				);
			},
		},
		{
			title: 'Employee Share',
			dataIndex: 'deduction Emp',
			key: 'deductionEmp',
			className: 'bg-white',
			width: 150,
			render: (text: any) =>
				formatNumber(text, profileData.CompanyCurrencies, false),
		},
		{
			title: 'Employer Share',
			dataIndex: 'deduction Empr',
			key: 'deductionEmpr',
			className: 'bg-white',
			width: 150,
			render: (text: any) =>
				formatNumber(text, profileData.CompanyCurrencies, false),
		},
		{
			title: 'Gross Pay',
			dataIndex: 'Total pay',
			key: 'totalPay',
			className: 'bg-white',
			width: 150,
			render: (text: any) =>
				formatNumber(text, profileData.CompanyCurrencies, false),
		},

		{
			title: 'Status',
			dataIndex: 'Status',
			className: 'bg-white',
			key: 'status',
			width: 150,
			render: (value: string, record: any) => {
				let statusText = '';
				let statusClass = '';
				let StatusIcon = null;

				switch (value) {
					case 'PENDING':
						statusText = 'Pending';
						statusClass = styles['status-pending'];
						StatusIcon = <PendingForApproval />;
						break;
					case 'FAILED':
						statusText = 'Failed';
						statusClass = styles['status-rejected'];
						StatusIcon = <RejectedByAccounts />;
						break;
					case 'SUCCESS':
						statusText = 'Success';
						statusClass = styles['status-success'];
						StatusIcon = <Paid />;
						break;
					case 'PROCESSING':
						statusClass = styles['status-success'];
						StatusIcon = <PaymentUnderProcessWithText />;
						break;
					default:
						statusText = 'Unknown Status';
						statusClass = styles['status-unknown'];
						StatusIcon = null;
				}

				return (
					<div className={styles['statusContainer']}>
						<div className={styles['statusDisplay']}>
							<Tag
								className={`${styles['statusTag']} ${statusClass}`}
								bordered={false}
							>
								<span className={styles['statusIcon']}>
									{StatusIcon}
								</span>
								<span>{statusText}</span>
							</Tag>
						</div>
					</div>
				);
			},
		},
		{
			title: 'Action',
			key: 'action',
			width: 150,
			className: 'bg-white',
			render: (_: any, record: any) => (
				<Popconfirm
					title="Are you sure to delete this record?"
					onConfirm={() => handleDelete(record.id)}
					okText="Yes"
					cancelText="No"
				>
					<p
						className="cursor-pointer flex align-center justify-center"
						style={{
							fontSize: 18,
							cursor: 'pointer',
						}}
					>
						<DeleteActionSvg />
					</p>
				</Popconfirm>
			),
		},
	];

	const handleDelete = (id: string) => {
		setNapsaData((prevData) => prevData.filter((item) => item.id !== id));
	};

	return (
		<Modal
			title=""
			open={isModalVisible}
			centered={true}
			onCancel={() => {
				if (current === 2) {
					fetchNapsaReportDetails();
				}
				handleCancel();
			}}
			footer={null}
			width="1400px"
			rootClassName="uploadNasaModal"
			closable={false}
			style={{
				maxHeight: 'calc(100vh - 200px)', // Fixed height for the modal
				// display: 'flex',
				flexDirection: 'column',
				// overflowY: 'scroll',
			}}
		>
			<div>
				<Steps current={current}>
					{steps.map((step, index) => (
						<Step key={index} title={step.title} />
					))}
				</Steps>
				<div
					style={{
						marginTop: '20px',
						marginBottom: '0px 20px 20px 20px',
						padding: '20px',
						border: '1px solid #d9d9d9',
						borderRadius: '5px',
						flex: 1,
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						minHeight: '200px',
						justifyContent: 'center',
					}}
				>
					{current === 0 && (
						<div className="napsa-submit-table">
							<Table
								columns={columns}
								loading={loading}
								dataSource={napsaData}
								scroll={{
									x: 'max-content',
									y: 'calc(100vh - 400px)',
								}}
								pagination={false}
								size="middle"
								bordered
								tableLayout="fixed"
								footer={() => (
									<div style={{ textAlign: 'right' }}>
										Total No. of Employees :{' '}
										{napsaData?.length || 0}
									</div>
								)}
							/>
						</div>
					)}

					{current === 1 && (
						<div className="napsa-submit-table">
							<Table
								columns={columns}
								loading={loading}
								dataSource={finalData ?? []}
								scroll={{
									x: 'max-content',
									y: 'calc(100vh - 400px)',
								}}
								pagination={false}
								size="middle"
								bordered
								tableLayout="fixed"
								footer={() => (
									<div style={{ textAlign: 'right' }}>
										Total No. of Employees :{' '}
										{finalData?.length || 0}
									</div>
								)}
							/>
						</div>
					)}
					{current === 2 && (
						<Result
							status="success"
							title="NAPSA Submission Initiated Successfully"
							subTitle={`The submission of ${napsaData.length} NAPSA records has been initiated. All records were validated and are now being processed in the background. This may take a few moments.`}
							icon={
								<CheckCircleFilled
									style={{ color: '#584495' }}
								/>
							}
						/>
					)}
				</div>

				{current !== 2 && (
					<div
						style={{
							marginTop: '20px',
							display: 'flex',
							justifyContent: 'end',
							gap: '15px',
						}}
					>
						<Button
							onClick={() => {
								if (current === 2) {
									fetchNapsaReportDetails();
								}
								handleCancel();
							}}
						>
							Cancel
						</Button>
						{current !== 0 && (
							<Button
								disabled={
									current === 0 || loading || isSubmitLoading
								}
								onClick={prev}
								type="primary"
							>
								Previous
							</Button>
						)}
						<Button
							disabled={
								current === steps.length - 1 || isSubmitLoading
								// finalData.length === 0
							}
							loading={isSubmitLoading}
							type="primary"
							onClick={next}
						>
							{current === 2 ? 'Submit Napsa Return' : 'Next'}
						</Button>
					</div>
				)}
			</div>
		</Modal>
	);
};
